import { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AuthenticatedQuestionnaire from './components/AuthenticatedQuestionnaire';
import SignupPage from './components/SignupPage';
import LoginPage from './components/LoginPage';
import Navigation from './components/Navigation';
import Dashboard from './components/Dashboard';
import FoldersApp from './components/FoldersApp';
import BudgetCalculatorApp from './components/BudgetCalculatorApp';
import Settings from './components/Settings';
import { AuthGuard } from './components/auth';
import { useAuth } from './contexts/AuthContext';

// Main App Layout Component
function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="app-layout">
      <div className="app-nav-wrapper">
        <Navigation />
      </div>
      {children}
    </div>
  );
}


// Public onboarding pages for non-authenticated users
function PublicPages() {
  const [currentStep, setCurrentStep] = useState<'signup' | 'login'>('signup');

  const handleGoToLogin = () => {
    setCurrentStep('login');
  };

  const handleBackToSignup = () => {
    setCurrentStep('signup');
  };

  if (currentStep === 'signup') {
    return (
      <SignupPage
        onLogin={handleGoToLogin}
      />
    );
  }

  return (
    <LoginPage
      onSignup={handleBackToSignup}
    />
  );
}

export default function App() {
  const { isLoading, isAuthenticated } = useAuth();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="text-center">
          <div className="app-spinner"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Public route for signup/login */}
        <Route path="/" element={
          isAuthenticated ? (
            <AppLayout>
              <Dashboard />
            </AppLayout>
          ) : (
            <PublicPages />
          )
        } />
        <Route path="/dashboard" element={
          <AuthGuard>
            <AppLayout>
              <Dashboard />
            </AppLayout>
          </AuthGuard>
        } />
        <Route path="/folders" element={
          <AuthGuard>
            <AppLayout>
              <FoldersApp />
            </AppLayout>
          </AuthGuard>
        } />
        <Route path="/budget" element={
          <AuthGuard>
            <AppLayout>
              <BudgetCalculatorApp />
            </AppLayout>
          </AuthGuard>
        } />
        <Route path="/questionnaire" element={
          <AuthGuard>
            <AuthenticatedQuestionnaire 
              onComplete={() => window.location.href = '/dashboard'} 
              onSkip={() => window.location.href = '/dashboard'} 
            />
          </AuthGuard>
        } />
        <Route path="/settings" element={
          <AuthGuard>
            <AppLayout>
              <Settings />
            </AppLayout>
          </AuthGuard>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}